import { useEffect, useRef, useState } from 'react';
import { usePharmacy } from '@/context/PharmacyContext';
import { Link } from 'react-router-dom';
import InThisPost from './InThisPost';

interface BlogSidebarProps {
  recentPosts?: { title: string; slug: string; published_at: string }[];
  categories?: string[];
  inThisPostContent?: string;
}

export default function BlogSidebar({ recentPosts = [], categories = [], inThisPostContent }: BlogSidebarProps) {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { settings } = usePharmacy();
  const [gsapLoaded, setGsapLoaded] = useState(false);

  useEffect(() => {
    let mm: any;
    import('gsap').then(({ default: gsap }) => {
      import('gsap/ScrollTrigger').then(({ ScrollTrigger }) => {
        gsap.registerPlugin(ScrollTrigger);

        if (sidebarRef.current) {
          mm = gsap.matchMedia();

          mm.add('(min-width: 1024px)', () => {
            ScrollTrigger.create({
              trigger: sidebarRef.current!.parentElement,
              start: 'top 100px',
              end: 'bottom bottom',
              pin: sidebarRef.current,
              pinSpacing: false,
            });
          });
        }

        setGsapLoaded(true);
      });
    });

    return () => {
      if (mm) mm.revert();
    };
  }, []);

  const fullAddress = settings ? `${settings.address_line1}, ${settings.city}, ${settings.province} ${settings.postal_code}` : '';

  return (
    <aside ref={sidebarRef} className="space-y-5 w-full lg:w-80 flex-shrink-0" style={{ willChange: 'transform' }}>

      {/* In This Post - navigation for current article */}
      {inThisPostContent && (
        <InThisPost contentHtml={inThisPostContent} />
      )}

      {/* About / Internal Links */}
      <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        <div className="bg-foreground-900 px-5 py-4">
          <h3 className="text-xs font-bold text-white tracking-[0.12em] uppercase flex items-center gap-2">
            <i className="ri-information-line text-accent-400"></i>
            About Our Blog
          </h3>
        </div>
        <div className="p-5">
          <p className="text-sm text-foreground-600 leading-relaxed mb-4">
            Expert insights on addiction treatment, pharmacy care, and wellness from {settings?.pharmacy_name || 'Thornhill Drug-Mart'}.
          </p>
          <div className="space-y-2">
            <Link to="/addiction-services" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-heart-pulse-line text-accent-500 text-sm"></i>
              <span>Our Treatment Programs</span>
            </Link>
            <Link to="/location" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-map-pin-line text-accent-500 text-sm"></i>
              <span>Visit Our Pharmacy</span>
            </Link>
            <Link to="/naloxone-kits" className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-first-aid-kit-line text-accent-500 text-sm"></i>
              <span>Free Naloxone Kits</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Posts */}
      {recentPosts.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="bg-foreground-900 px-5 py-4">
            <h3 className="text-xs font-bold text-white tracking-[0.12em] uppercase flex items-center gap-2">
              <i className="ri-history-line text-accent-400"></i>
              Recent Posts
            </h3>
          </div>
          <ul className="divide-y divide-background-100">
            {recentPosts.map((post) => (
              <li key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  className="block px-5 py-3.5 text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <p className="font-medium line-clamp-2">{post.title}</p>
                  <p className="text-xs text-foreground-500 mt-1">
                    {new Date(post.published_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="bg-foreground-900 px-5 py-4">
            <h3 className="text-xs font-bold text-white tracking-[0.12em] uppercase flex items-center gap-2">
              <i className="ri-folder-line text-accent-400"></i>
              Categories
            </h3>
          </div>
          <ul className="divide-y divide-background-100">
            {categories.map((cat) => (
              <li key={cat}>
                <span className="flex items-center justify-between px-5 py-3 text-sm text-foreground-600 font-medium hover:bg-background-50 transition-colors cursor-pointer">
                  <span className="flex items-center gap-2">
                    <i className="ri-hashtag text-accent-500 text-xs"></i>
                    {cat}
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Banners */}
      <div className="bg-gradient-to-br from-accent-500 to-accent-600 rounded-xl p-5 text-background-50 dark:text-foreground-950">
        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-3">
          <i className="ri-phone-line text-xl"></i>
        </div>
        <h4 className="text-sm font-bold mb-1">Need Help?</h4>
        <p className="text-xs text-white/80 leading-relaxed mb-4">
          Our team is available 7 days a week to support your recovery journey.
        </p>
        <a
          href={`tel:${settings?.phone_raw || '9057099985'}`}
          className="block text-center bg-background-50 text-accent-600 hover:bg-background-50/90 text-sm font-semibold py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          {settings?.phone || '(905) 709-9985'}
        </a>
      </div>

      {/* Location Banner */}
      <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        <div className="w-full h-32 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20pharmacy%20storefront%20exterior%20Thornhill%20Ontario%20professional%20healthcare%20building%20clean%20architecture%20warm%20welcoming%20entrance%20daytime&width=400&height=200&seq=blog-sidebar-loc-v1&orientation=landscape"
            alt={settings?.pharmacy_name || 'Thornhill Drug-Mart'}
            className="w-full h-full object-cover object-top"
            width={400}
            height={160}
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-1">Find Us</h4>
          <p className="text-xs text-foreground-500 leading-relaxed mb-3">{fullAddress}</p>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
          >
            <i className="ri-navigation-line"></i>
            Get Directions
          </a>
        </div>
      </div>
    </aside>
  );
}